import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Skeleton,
  Tooltip,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  TableOutlined,
  TagOutlined,
} from '@ant-design/icons';
import DashboardPageWrapper from '../../../../components/layout/DashboardPageWrapper';
import { useExplicitPermission } from '../../../../contexts/AuthContext';
import { PERMISSIONS } from '../../../../config/permissions';
import ifaService from '../../../../services/ifaService';
import productService from '../../../../services/productService';
import usePricingResolve from '../../../../hooks/usePricingResolve';
import useRemoteSelectPage from '../../../../hooks/useRemoteSelectPage';
import orderService from '../../../../services/orderService';
import investorProfileService from '../../../../services/investorProfileService';
import { getAccountUuid } from '../../../../utils/orderAccountUtils';
import PlaceOrderBankDematModal from './PlaceOrderBankDematModal';
import PlaceOrderSuccessModal from './PlaceOrderSuccessModal';
import { INVESTOR_PROFILE_TRANSACTIONS_TAB } from '../../../investors/commons/investorProfile.helpers';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getPartnerUuid(p) { return p?.id ?? p?.uuid ?? ''; }
function getInvestorUuid(inv) { return inv?.id ?? inv?.uuid ?? ''; }
function getProductId(product) { return product?.id ?? ''; }

/** Ant Design 6 nests custom option fields under `data`. */
function getSelectOptionData(option) {
  const opt = Array.isArray(option) ? option[0] : option;
  return opt?.data ?? opt ?? {};
}

const INR = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 });
function fmtInr(v) { const n = Number(v); return Number.isFinite(n) ? INR.format(n) : '—'; }

/** Non-interactive price display for form fields the user cannot edit. */
function ReadOnlyPriceInput({ value }) {
  const display =
    value != null && value !== '' && Number.isFinite(Number(value))
      ? Number(value).toFixed(2)
      : null;
  return (
    <div className="flex h-8 w-full cursor-default select-none items-center rounded-md border border-gray-200 bg-gray-50 px-[11px] text-sm text-gray-700">
      <span className="mr-1 text-gray-400">₹</span>
      <span>{display ?? 'Auto'}</span>
    </div>
  );
}

function initials(str) { return (str || '?').trim().substring(0, 2).toUpperCase(); }

const LABEL_CLS =
  '[&_.ant-form-item-label>label]:text-xs [&_.ant-form-item-label>label]:font-semibold [&_.ant-form-item-label>label]:uppercase [&_.ant-form-item-label>label]:tracking-wide [&_.ant-form-item-label>label]:text-gray-500 [&_.ant-form-item-label]:pb-1.5';

/** Taller selector so stacked name / meta rows have room (partner, investor, share). */
const ENTITY_SELECT_CLS =
  'w-full order-entity-select [&_.ant-select-selector]:!min-h-[76px] [&_.ant-select-selector]:!h-auto [&_.ant-select-selector]:!items-center [&_.ant-select-selector]:!py-2.5 [&_.ant-select-selector]:!px-3 [&_.ant-select-selection-item]:!h-auto [&_.ant-select-selection-item]:!max-h-none [&_.ant-select-selection-item]:!overflow-visible [&_.ant-select-selection-item]:!whitespace-normal [&_.ant-select-selection-item]:!leading-normal [&_.ant-select-selection-item]:!py-0';

const ENTITY_OPTION_PAD = 'py-2.5';
const SELECT_PAGE_SIZE = 50;

function EntityAvatar({ label, imageUrl, variant = 'primary', rounded = 'full' }) {
  const sz = 'h-9 w-9 shrink-0 text-xs font-semibold';
  const round = rounded === 'md' ? 'rounded-md' : 'rounded-full';
  const tones = {
    primary: 'bg-primary-muted text-primary',
    indigo: 'bg-indigo-100 text-indigo-700',
    slate: 'bg-slate-100 text-slate-600',
  };

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className={`${sz} ${round} border border-gray-100 object-cover bg-white`}
      />
    );
  }

  return (
    <span className={`${sz} ${round} flex items-center justify-center ${tones[variant] ?? tones.primary}`}>
      {initials(label)}
    </span>
  );
}

function StackedEntityOption({ imageUrl, title, line2, line3, variant = 'primary', imageRounded = 'full' }) {
  if (!title) return null;
  return (
    <div className={`flex items-start gap-3 ${ENTITY_OPTION_PAD}`}>
      <EntityAvatar label={title} imageUrl={imageUrl} variant={variant} rounded={imageRounded} />
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="truncate text-sm font-medium leading-tight text-gray-900">{title}</p>
        {line2 ? (
          <p className="mt-1 truncate text-xs leading-tight text-gray-600">{line2}</p>
        ) : null}
        {line3 ? (
          <p className="mt-0.5 truncate text-xs leading-tight text-gray-400">{line3}</p>
        ) : null}
      </div>
    </div>
  );
}

function buildPartnerMeta(partner) {
  if (!partner) return null;
  const title = partner.name || partner.email || '—';
  return {
    title,
    line2: partner.pan || partner.email || partner.display_id || null,
    line3: partner.mobile || null,
    searchLabel: [title, partner.email, partner.display_id, partner.pan, partner.mobile]
      .filter(Boolean)
      .join(' '),
  };
}

function buildInvestorMeta(investor) {
  if (!investor) return null;
  const title = investor.name || '—';
  return {
    title,
    line2: investor.pan || null,
    line3: investor.mobile || null,
    searchLabel: [title, investor.pan, investor.mobile, investor.folio_id].filter(Boolean).join(' '),
  };
}

function buildShareMeta(product) {
  if (!product) return null;
  const title = product.name || product.short_name || '—';
  const short = product.short_name && product.short_name !== title ? product.short_name : null;
  const isin = product.isin_numbers?.[0] || null;
  const industry = product.industry_name || null;
  return {
    title,
    line2: short || industry || null,
    line3: isin ? `ISIN ${isin}` : null,
    imageUrl: product.logo_url,
    searchLabel: [title, short, isin, industry, ...(product.isin_numbers || [])]
      .filter(Boolean)
      .join(' '),
  };
}

// ─── Exclusive pricing callout ────────────────────────────────────────────────

function ExclusiveCallout({ specialPricing, appliedSlab, onViewChart }) {
  const [dismissed, setDismissed] = useState(false);
  const prevSlabRef = useRef(null);

  // Re-show callout when product/partner changes (slab resets to null)
  useEffect(() => {
    if (prevSlabRef.current !== null && appliedSlab === null) setDismissed(false);
    prevSlabRef.current = appliedSlab;
  }, [appliedSlab]);

  if (!specialPricing?.length || dismissed) return null;

  const minPrice = Math.min(
    ...(specialPricing.map((s) => Number(s.pricing_per_unit)).filter((n) => Number.isFinite(n) && n > 0))
  );
  const specialApplied = Boolean(appliedSlab?.is_this_special_price_slab);

  const title = specialApplied
    ? 'Exclusive pricing applied for this order'
    : 'Exclusive pricing available for this order';

  return (
    <div className="relative mb-4 overflow-hidden rounded-xl border border-teal-200 bg-teal-50 px-4 py-4 pr-10">
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-md border border-teal-200 bg-white text-teal-500 transition-colors hover:bg-teal-100"
        aria-label="Dismiss"
      >
        <CloseOutlined style={{ fontSize: 10 }} />
      </button>

      {/* Sparkle icon */}
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-white text-xs">
          ✦
        </span>
        <span className="text-sm font-semibold text-teal-800">{title}</span>
      </div>

      {specialApplied ? (
        <p className="text-sm text-teal-700">
          Exclusive rate{' '}
          <strong className="font-semibold">{fmtInr(appliedSlab.pricing_per_unit)}/unit</strong>{' '}
          applied ({appliedSlab.min_qty}–{appliedSlab.max_qty ?? '∞'} units).
        </p>
      ) : (
        <p className="text-sm leading-relaxed text-teal-700">
          Tiers start from
          <br />
          <strong className="text-base font-bold text-teal-900">
            {Number.isFinite(minPrice) ? fmtInr(minPrice) : '—'}/unit
          </strong>
          <br />
          for qualifying quantities.{' '}
          <button
            type="button"
            onClick={onViewChart}
            className="font-medium text-teal-900 underline underline-offset-2 hover:opacity-80"
          >
            Open price chart →
          </button>
        </p>
      )}
    </div>
  );
}

// ─── Active tier bar ──────────────────────────────────────────────────────────

function TierBar({ appliedSlab, basePartnerPrice, onViewChart }) {
  const price = appliedSlab
    ? fmtInr(appliedSlab.pricing_per_unit)
    : basePartnerPrice != null
    ? fmtInr(basePartnerPrice)
    : null;

  const range = appliedSlab
    ? `${appliedSlab.min_qty}–${appliedSlab.max_qty ?? '∞'} units`
    : null;

  return (
    <div className="mb-4 flex flex-col gap-2.5 rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <div className="flex min-w-0 items-start gap-2.5 sm:flex-1">
        <TagOutlined className="mt-0.5 shrink-0 text-gray-400 sm:mt-0" />
        <div className="min-w-0">
          {price ? (
            <>
              <p className="truncate text-sm font-semibold leading-tight text-gray-900">
                {price}/unit
              </p>
              {range ? (
                <p className="mt-0.5 truncate text-xs leading-tight text-gray-400">{range}</p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-gray-400">Enter quantity to see applicable rate</p>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onViewChart}
        className="flex w-full shrink-0 items-center justify-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:text-gray-800 sm:w-auto sm:justify-start"
      >
        <TableOutlined />
        Price chart
      </button>
    </div>
  );
}

// ─── Price chart modal ────────────────────────────────────────────────────────

function SlabRow({ slab, isApplied, exclusiveTier = false }) {
  const label =
    slab.max_qty == null || slab.max_qty === 0
      ? `${slab.min_qty}+ units`
      : `${slab.min_qty} – ${slab.max_qty} units`;

  const appliedShell = exclusiveTier
    ? 'border border-teal-300 bg-teal-50'
    : 'border border-primary bg-primary-muted';
  const appliedLabel = exclusiveTier ? 'font-medium text-teal-800' : 'font-medium text-primary';
  const appliedPrice = exclusiveTier ? 'text-teal-900' : 'text-gray-900';
  const appliedCheck = exclusiveTier ? 'text-teal-600' : 'text-primary';

  return (
    <div
      className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors ${
        isApplied ? appliedShell : 'border border-transparent hover:bg-gray-50'
      }`}
    >
      <span className={isApplied ? appliedLabel : 'text-gray-600'}>{label}</span>
      <span className={`flex items-center gap-2 font-semibold ${isApplied ? appliedPrice : 'text-gray-900'}`}>
        {fmtInr(slab.pricing_per_unit)}/unit
        {isApplied ? <CheckOutlined className={appliedCheck} style={{ fontSize: 11 }} /> : null}
      </span>
    </div>
  );
}

function PriceChartModal({ open, onClose, pricing, specialPricing, appliedSlab }) {
  const sortedSpecial = useMemo(
    () => [...specialPricing].sort((a, b) => (a.tier_order ?? 0) - (b.tier_order ?? 0)),
    [specialPricing]
  );
  const sortedRegular = useMemo(
    () => [...pricing].sort((a, b) => (a.tier_order ?? 0) - (b.tier_order ?? 0)),
    [pricing]
  );

  return (
    <Modal open={open} onCancel={onClose} footer={null} title="Price chart" width={460}>
      <div className="space-y-5 pt-1">
        {sortedSpecial.length > 0 ? (
          <div>
            <div className="mb-2 flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-teal-600">
                Exclusive tiers
              </span>
            </div>
            <div className="space-y-1 rounded-xl bg-teal-50/60 p-2">
              {sortedSpecial.map((s) => (
                <SlabRow key={s.id} slab={s} isApplied={appliedSlab?.id === s.id} exclusiveTier />
              ))}
            </div>
          </div>
        ) : null}
        <div>
          <div className="mb-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Volume pricing
            </span>
          </div>
          <div className="space-y-1">
            {sortedRegular.length ? (
              sortedRegular.map((s) => (
                <SlabRow key={s.id} slab={s} isApplied={appliedSlab?.id === s.id} />
              ))
            ) : (
              <p className="py-6 text-center text-sm text-gray-400">No tiers available.</p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ─── Summary ──────────────────────────────────────────────────────────────────

function SummaryRow({ label, value, sub, highlight }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3 [&+&]:border-t [&+&]:border-gray-200">
      <span className="text-sm text-gray-500">{label}</span>
      <div className="text-right tabular-nums">
        <p className={`text-sm font-medium ${highlight === 'green' ? 'text-primary' : 'text-gray-900'}`}>
          {value}
        </p>
        {sub ? (
          <p className="mt-0.5 text-xs font-semibold text-primary">{sub}</p>
        ) : null}
      </div>
    </div>
  );
}

function OrderSummary({
  quantity,
  partnerSellPrice,
  investorSellPrice,
  canPlaceOrder,
  onPlaceOrder,
  placing = false,
}) {
  let pricePerUnit = '—';
  let totalShares = '—';
  let marginAmount = '—';
  let marginPct = null;

  if (canPlaceOrder) {
    const qty = Number(quantity);
    const psp = Number(partnerSellPrice);
    const isp = Number(investorSellPrice);
    const totalPartner = qty * psp;
    const totalInvestor = qty * isp;
    const margin = totalInvestor - totalPartner;

    pricePerUnit = fmtInr(isp);
    totalShares = fmtInr(totalInvestor);
    marginAmount = fmtInr(margin);
    marginPct = totalPartner > 0 ? ((margin / totalPartner) * 100).toFixed(2) : null;
  }

  return (
    <>
      <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-1 sm:px-5">
        <p className="border-b border-gray-200 py-3 text-xs font-bold uppercase tracking-widest text-gray-500">
          Summary
        </p>
        <SummaryRow label="Price per unit" value={pricePerUnit} />
        <SummaryRow
          label="Total price of shares"
          value={totalShares}
          highlight={canPlaceOrder ? 'green' : undefined}
        />
        <SummaryRow
          label="Margin (% premium to partner price)"
          value={marginAmount}
          sub={marginPct != null ? `${marginPct}%` : undefined}
        />
      </div>

      <Tooltip title={canPlaceOrder ? undefined : 'Fill all fields with valid values'}>
        <Button
          type="primary"
          size="large"
          block
          disabled={!canPlaceOrder || placing}
          loading={placing}
          onClick={onPlaceOrder}
          className="mt-3 h-12! rounded-xl! text-base! font-semibold!"
        >
          Place order
        </Button>
      </Tooltip>
    </>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PlaceSingleShareOrderPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const partnerUuidFromUrl = searchParams.get('partner_uuid')?.trim() || '';
  const investorIdFromUrl = searchParams.get('investor_id')?.trim() || '';
  const productIdFromUrl = searchParams.get('product_id')?.trim() || '';

  const [form] = Form.useForm();
  const selectedPartnerUuid = Form.useWatch('partner_uuid', form);
  const selectedInvestorId = Form.useWatch('investor_id', form);
  const selectedProductId = Form.useWatch('product_id', form);

  const [loadError, setLoadError] = useState('');

  const partnerPrefilledRef = useRef(false);
  const investorPrefilledRef = useRef(false);
  const productPrefilledRef = useRef(false);
  const [chartOpen, setChartOpen] = useState(false);
  const [accountsModalOpen, setAccountsModalOpen] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [selectedPartnerRecord, setSelectedPartnerRecord] = useState(null);
  const [selectedInvestorRecord, setSelectedInvestorRecord] = useState(null);
  const [selectedShareRecord, setSelectedShareRecord] = useState(null);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [placedOrderContext, setPlacedOrderContext] = useState({
    investorId: '',
    partnerUuid: '',
    investorName: '',
  });

  const pricingEnabled =
    Boolean(selectedPartnerUuid) && Boolean(selectedInvestorId) && Boolean(selectedProductId);

  const {
    pricing, specialPricing, appliedSlab,
    basePartnerPrice, partnerPricePerUnit,
    effectiveMinQty, effectiveMaxQty,
    validationMessage, hasResolveSuccess, canProceed, canBypassSlabLimits,
    loading: pricingLoading, error: pricingError,
    quantity, setQuantity, retry: retryPricing,
  } = usePricingResolve({
    productId: selectedProductId || '',
    partnerId: selectedPartnerUuid || '',
    userId: selectedInvestorId || '',
    enabled: pricingEnabled,
  });

  /** Editable only with explicit permission — not opened via super-admin bypass. */
  const canEditPartnerPrice = useExplicitPermission(PERMISSIONS.ORDERS_CHANGE_PARTNER_PRICE);
  const canEditPartnerPriceField = canEditPartnerPrice || canBypassSlabLimits;
  const partnerSellPrice = Form.useWatch('partner_sell_price', form);
  const investorSellPrice = Form.useWatch('investor_sell_price', form);

  const isPartnerSameAsInvestor = useMemo(
    () =>
      Boolean(
        selectedPartnerUuid &&
          selectedInvestorId &&
          String(selectedPartnerUuid) === String(selectedInvestorId)
      ),
    [selectedPartnerUuid, selectedInvestorId]
  );

  useEffect(() => {
    form.setFieldValue(
      'partner_sell_price',
      partnerPricePerUnit !== null ? partnerPricePerUnit : undefined
    );
  }, [partnerPricePerUnit, form]);

  useEffect(() => {
    if (!isPartnerSameAsInvestor) return;
    const price = partnerSellPrice ?? partnerPricePerUnit;
    if (price != null && price !== '') {
      form.setFieldValue('investor_sell_price', price);
    } else {
      form.setFieldValue('investor_sell_price', undefined);
    }
  }, [isPartnerSameAsInvestor, partnerSellPrice, partnerPricePerUnit, form]);

  useEffect(() => {
    form.setFieldsValue({ quantity: undefined, partner_sell_price: undefined, investor_sell_price: undefined });
    setQuantity('');
  }, [selectedPartnerUuid, selectedInvestorId, selectedProductId, form, setQuantity]);

  const canPlaceOrder = useMemo(() => {
    const qty = Number(quantity);
    const psp = Number(partnerSellPrice);
    const isp = Number(investorSellPrice);
    const slabMin = Number(appliedSlab?.pricing_per_unit);
    const priceFloorOk = canBypassSlabLimits || !Number.isFinite(slabMin) || psp >= slabMin;
    return (
      canProceed && !validationMessage &&
      Number.isFinite(qty) && qty > 0 &&
      Number.isFinite(psp) && psp > 0 && priceFloorOk &&
      Number.isFinite(isp) && isp >= psp
    );
  }, [canProceed, validationMessage, quantity, partnerSellPrice, investorSellPrice, appliedSlab, canBypassSlabLimits]);

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchPartnersPage = useCallback(async ({ page, limit, search, signal }) => {
    setLoadError('');
    const res = await ifaService.getAdminIfas({
      page,
      limit,
      search,
      status: 'active',
      signal,
    });
    return {
      items: Array.isArray(res?.users) ? res.users : [],
      pagination: res?.pagination,
    };
  }, []);

  const fetchInvestorsPage = useCallback(
    async ({ page, limit, search, signal }) => {
      if (!selectedPartnerUuid) return { items: [], pagination: null };
      const res = await ifaService.getPartnerProfileSection(selectedPartnerUuid, 'investors', {
        page,
        limit,
        search,
        signal,
      });
      const list = Array.isArray(res?.data) ? res.data : [];
      return {
        items: list.map((inv) => ({
          id: inv.uuid,
          name: inv.name,
          email: inv.email,
          mobile: inv.mobile,
          pan: inv.pan,
          folio_id: inv.folio_id,
        })),
        pagination: res?.pagination,
      };
    },
    [selectedPartnerUuid]
  );

  const fetchProductsPage = useCallback(async ({ page, limit, search, signal }) => {
    const r = await productService.getProducts({
      type: 'PRE_IPO',
      page,
      limit,
      search,
      sort_by: 'preference_index',
      sort_order: 'asc',
      signal,
    });
    const d = r?.data ?? r;
    const list = d?.products ?? d?.data ?? d?.items ?? [];
    return {
      items: Array.isArray(list) ? list : [],
      pagination: d?.pagination,
    };
  }, []);

  const {
    items: partners,
    loading: loadingPartners,
    handleSearch: handlePartnerSearch,
    handlePopupScroll: handlePartnerPopupScroll,
    handleOpenChange: handlePartnerOpenChange,
    dropdownRender: partnerDropdownRender,
    searchValue: partnerSearchValue,
    clearSearch: clearPartnerSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchPartnersPage,
    getKey: getPartnerUuid,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: selectedPartnerUuid,
    prefetchOnMount: Boolean(partnerUuidFromUrl),
    prefetchSearch: partnerUuidFromUrl,
    onError: (e) => setLoadError(e?.response?.data?.message || e?.message || 'Failed to load partners'),
  });

  const {
    items: investors,
    loading: loadingInvestors,
    handleSearch: handleInvestorSearch,
    handlePopupScroll: handleInvestorPopupScroll,
    handleOpenChange: handleInvestorOpenChange,
    dropdownRender: investorDropdownRender,
    searchValue: investorSearchValue,
    clearSearch: clearInvestorSearch,
  } = useRemoteSelectPage({
    enabled: Boolean(selectedPartnerUuid),
    fetchPage: fetchInvestorsPage,
    getKey: getInvestorUuid,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: selectedInvestorId,
    prefetchOnMount: Boolean(investorIdFromUrl),
    prefetchSearch: investorIdFromUrl,
    onError: () => message.error('Failed to load investors'),
  });

  const {
    items: products,
    loading: loadingProducts,
    handleSearch: handleProductSearch,
    handlePopupScroll: handleProductPopupScroll,
    handleOpenChange: handleShareOpenChange,
    dropdownRender: shareDropdownRender,
    searchValue: shareSearchValue,
    clearSearch: clearShareSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchProductsPage,
    getKey: getProductId,
    pageSize: SELECT_PAGE_SIZE,
    selectedValue: selectedProductId,
    prefetchOnMount: Boolean(productIdFromUrl),
    prefetchSearch: productIdFromUrl,
    onError: () => message.error('Failed to load shares'),
  });

  // ── Prefill ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (partnerPrefilledRef.current || !partnerUuidFromUrl || !partners.length) return;
    const m = partners.find((p) => String(getPartnerUuid(p)) === String(partnerUuidFromUrl));
    if (!m) return;
    partnerPrefilledRef.current = true;
    setSelectedPartnerRecord(m);
    form.setFieldValue('partner_uuid', getPartnerUuid(m));
    handlePartnerSearch('');
  }, [partnerUuidFromUrl, partners, form, handlePartnerSearch]);

  useEffect(() => {
    if (investorPrefilledRef.current || !investorIdFromUrl || !selectedPartnerUuid || loadingInvestors) return;
    const m = investors.find((inv) => String(getInvestorUuid(inv)) === String(investorIdFromUrl));
    if (!m) return;
    investorPrefilledRef.current = true;
    setSelectedInvestorRecord(m);
    form.setFieldValue('investor_id', getInvestorUuid(m));
    handleInvestorSearch('');
  }, [investorIdFromUrl, investors, selectedPartnerUuid, loadingInvestors, form, handleInvestorSearch]);

  useEffect(() => {
    if (productPrefilledRef.current || !productIdFromUrl || !products.length || loadingProducts) return;
    const m = products.find((p) => String(p.id) === productIdFromUrl);
    if (!m) return;
    productPrefilledRef.current = true;
    setSelectedShareRecord(m);
    form.setFieldValue('product_id', m.id);
    handleProductSearch('');
  }, [productIdFromUrl, products, loadingProducts, form, handleProductSearch]);

  // ── URL sync ─────────────────────────────────────────────────────────────────

  const syncToUrl = useCallback((updates = {}) => {
    const hasUpdate = (key) => Object.prototype.hasOwnProperty.call(updates, key);
    const pu = hasUpdate('partnerUuid') ? updates.partnerUuid : form.getFieldValue('partner_uuid');
    const ii = hasUpdate('investorId') ? updates.investorId : form.getFieldValue('investor_id');
    const pi = hasUpdate('productId') ? updates.productId : form.getFieldValue('product_id');
    const p = new URLSearchParams();
    if (pu) p.set('partner_uuid', String(pu));
    if (ii) p.set('investor_id', String(ii));
    if (pi) p.set('product_id', String(pi));
    setSearchParams(p, { replace: true });
  }, [setSearchParams, form]);

  const handlePartnerChange = (v, option) => {
    const data = getSelectOptionData(option);
    const nextPartner =
      data?.partner ?? partners.find((p) => String(getPartnerUuid(p)) === String(v)) ?? null;
    setSelectedPartnerRecord(v ? nextPartner : null);
    setSelectedInvestorRecord(null);
    investorPrefilledRef.current = true;
    form.setFieldsValue({ partner_uuid: v, investor_id: undefined });
    clearPartnerSearch();
    syncToUrl({ partnerUuid: v, investorId: undefined });
  };
  const handleInvestorChange = (v, option) => {
    const data = getSelectOptionData(option);
    const nextInvestor =
      data?.investor ?? investors.find((inv) => String(getInvestorUuid(inv)) === String(v)) ?? null;
    setSelectedInvestorRecord(v ? nextInvestor : null);
    clearInvestorSearch();
    syncToUrl({ investorId: v });
  };
  const handleProductChange = (v, option) => {
    const data = getSelectOptionData(option);
    const nextShare =
      data?.product ?? products.find((p) => String(p.id) === String(v)) ?? null;
    setSelectedShareRecord(v ? nextShare : null);
    clearShareSearch();
    syncToUrl({ productId: v });
  };

  // ── Derived ───────────────────────────────────────────────────────────────────

  const partnerOptions = useMemo(() => {
    const base = partners.map((p) => {
      const meta = buildPartnerMeta(p);
      return {
        value: getPartnerUuid(p),
        label: meta?.searchLabel || getPartnerUuid(p),
        partner: p,
      };
    });
    if (
      selectedPartnerRecord &&
      selectedPartnerUuid &&
      !base.some((o) => String(o.value) === String(selectedPartnerUuid))
    ) {
      const meta = buildPartnerMeta(selectedPartnerRecord);
      return [
        {
          value: getPartnerUuid(selectedPartnerRecord),
          label: meta?.searchLabel || String(selectedPartnerUuid),
          partner: selectedPartnerRecord,
        },
        ...base,
      ];
    }
    return base;
  }, [partners, selectedPartnerRecord, selectedPartnerUuid]);

  const investorOptions = useMemo(() => {
    const base = investors.map((inv) => {
      const meta = buildInvestorMeta(inv);
      return {
        value: getInvestorUuid(inv),
        label: meta?.searchLabel || getInvestorUuid(inv),
        investor: inv,
      };
    });
    if (
      selectedInvestorRecord &&
      selectedInvestorId &&
      !base.some((o) => String(o.value) === String(selectedInvestorId))
    ) {
      const meta = buildInvestorMeta(selectedInvestorRecord);
      return [
        {
          value: getInvestorUuid(selectedInvestorRecord),
          label: meta?.searchLabel || String(selectedInvestorId),
          investor: selectedInvestorRecord,
        },
        ...base,
      ];
    }
    return base;
  }, [investors, selectedInvestorRecord, selectedInvestorId]);

  const shareOptions = useMemo(() => {
    const base = products.map((p) => {
      const meta = buildShareMeta(p);
      return {
        value: p.id,
        label: meta?.searchLabel || String(p.id),
        product: p,
      };
    });
    if (
      selectedShareRecord &&
      selectedProductId &&
      !base.some((o) => String(o.value) === String(selectedProductId))
    ) {
      const meta = buildShareMeta(selectedShareRecord);
      return [
        {
          value: selectedShareRecord.id,
          label: meta?.searchLabel || String(selectedProductId),
          product: selectedShareRecord,
        },
        ...base,
      ];
    }
    return base;
  }, [products, selectedShareRecord, selectedProductId]);

  const selectedPartner =
    selectedPartnerRecord && String(getPartnerUuid(selectedPartnerRecord)) === String(selectedPartnerUuid)
      ? selectedPartnerRecord
      : partners.find((p) => String(getPartnerUuid(p)) === String(selectedPartnerUuid)) ?? null;
  const selectedInvestor =
    selectedInvestorRecord && String(getInvestorUuid(selectedInvestorRecord)) === String(selectedInvestorId)
      ? selectedInvestorRecord
      : investors.find((inv) => String(getInvestorUuid(inv)) === String(selectedInvestorId)) ?? null;
  const selectedShare =
    selectedShareRecord && String(selectedShareRecord.id) === String(selectedProductId)
      ? selectedShareRecord
      : products.find((p) => String(p.id) === String(selectedProductId)) ?? null;

  const qtyPlaceholder = effectiveMaxQty
    ? `${effectiveMinQty} – ${effectiveMaxQty}`
    : `min ${effectiveMinQty}`;

  const resetOrderForm = useCallback(() => {
    partnerPrefilledRef.current = false;
    investorPrefilledRef.current = false;
    productPrefilledRef.current = false;
    setSelectedPartnerRecord(null);
    setSelectedInvestorRecord(null);
    setSelectedShareRecord(null);
    form.resetFields();
    setQuantity('');
    setSearchParams({}, { replace: true });
    setSuccessModalOpen(false);
    setPlacedOrderContext({ investorId: '', partnerUuid: '', investorName: '' });
  }, [form, setQuantity, setSearchParams]);

  const handleGoToTransactions = useCallback(() => {
    const { investorId, partnerUuid } = placedOrderContext;
    if (!investorId) return;
    const params = new URLSearchParams({ tab: INVESTOR_PROFILE_TRANSACTIONS_TAB });
    if (partnerUuid) params.set('partner_uuid', partnerUuid);
    navigate(`/investors/${encodeURIComponent(investorId)}/profile?${params.toString()}`);
  }, [placedOrderContext, navigate]);

  const submitPlaceOrder = useCallback(
    async ({ bankAccountId, dematAccountId }) => {
      if (!canPlaceOrder) return;
      setPlacingOrder(true);
      try {
        const payload = {
          product_id: selectedProductId,
          quantity: Number(quantity),
          investor_price: Number(investorSellPrice),
          bank_account_id: bankAccountId,
          demat_account_id: dematAccountId,
        };
        if (canEditPartnerPriceField) {
          payload.partner_price = Number(partnerSellPrice);
        }
        await orderService.adminPlaceSellOrder(
          selectedInvestorId,
          selectedPartnerUuid,
          payload
        );
        setPlacedOrderContext({
          investorId: selectedInvestorId,
          partnerUuid: selectedPartnerUuid,
          investorName: selectedInvestor?.name || '',
        });
        setAccountsModalOpen(false);
        setSuccessModalOpen(true);
      } catch (e) {
        message.error(e?.response?.data?.message || e?.message || 'Could not place order');
      } finally {
        setPlacingOrder(false);
      }
    },
    [
      canPlaceOrder,
      selectedProductId,
      selectedInvestorId,
      selectedPartnerUuid,
      quantity,
      partnerSellPrice,
      investorSellPrice,
      canEditPartnerPriceField,
      selectedInvestor?.name,
    ]
  );

  const handleConfirmPlaceOrder = useCallback(
    ({ bankAccountId, dematAccountId }) => {
      if (placingOrder) return;
      submitPlaceOrder({ bankAccountId, dematAccountId });
    },
    [placingOrder, submitPlaceOrder]
  );

  const handleOpenPlaceOrder = useCallback(async () => {
    if (!canPlaceOrder || placingOrder) return;

    if (!selectedInvestorId) {
      setAccountsModalOpen(true);
      return;
    }

    setPlacingOrder(true);
    try {
      const data = await investorProfileService.getBankAndDematForOrder(selectedInvestorId);
      const bankList = Array.isArray(data?.bank_details) ? data.bank_details : [];
      const dematList = Array.isArray(data?.demat_details) ? data.demat_details : [];

      if (bankList.length === 1 && dematList.length === 1) {
        const bankAccountId = getAccountUuid(bankList[0]);
        const dematAccountId = getAccountUuid(dematList[0]);
        if (bankAccountId && dematAccountId) {
          await submitPlaceOrder({ bankAccountId, dematAccountId });
          return;
        }
      }

      setPlacingOrder(false);
      setAccountsModalOpen(true);
    } catch (e) {
      setPlacingOrder(false);
      message.error(
        e?.response?.data?.message || e?.message || 'Failed to load bank and demat details.'
      );
      setAccountsModalOpen(true);
    }
  }, [canPlaceOrder, placingOrder, selectedInvestorId, submitPlaceOrder]);

  const backToProfile = () => {
    const inv = form.getFieldValue('investor_id') || investorIdFromUrl;
    const prt = form.getFieldValue('partner_uuid') || partnerUuidFromUrl;
    if (inv) {
      navigate(`/investors/${encodeURIComponent(inv)}/profile${prt ? `?partner_uuid=${encodeURIComponent(prt)}` : ''}`);
      return;
    }
    navigate('/pre-ipo/order-management');
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <DashboardPageWrapper>
      <div className="mx-auto max-w-3xl">
        {loadError ? <Alert type="error" showIcon message={loadError} className="mb-3" /> : null}

        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
          {/* Compact title row — back + heading inside the card */}
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-2.5 sm:px-5">
            <button
              type="button"
              onClick={backToProfile}
              aria-label="Go back"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
            >
              <ArrowLeftOutlined style={{ fontSize: 14 }} />
            </button>
            <h1 className="text-base font-semibold text-gray-900">Place order</h1>
          </div>
          <div className="p-4 sm:p-5">
            <Form form={form} layout="vertical" requiredMark={false} className={LABEL_CLS}>

              {/* ── Selects ── */}
              <Row gutter={[12, 0]}>
                <Col xs={24} sm={12}>
                  <Form.Item name="partner_uuid" label="Partner" className="mb-3!">
                    <Select
                      showSearch
                      autoClearSearchValue={false}
                      allowClear={!partnerUuidFromUrl}
                      loading={loadingPartners}
                      placeholder="Select partner"
                      optionFilterProp="label"
                      filterOption={false}
                      searchValue={partnerSearchValue}
                      onSearch={handlePartnerSearch}
                      onPopupScroll={handlePartnerPopupScroll}
                      onOpenChange={handlePartnerOpenChange}
                      dropdownRender={partnerDropdownRender}
                      onChange={handlePartnerChange}
                      className={ENTITY_SELECT_CLS}
                      options={partnerOptions}
                      listHeight={360}
                      popupMatchSelectWidth
                      styles={{ popup: { root: { paddingBlock: 4 } } }}
                      optionRender={(opt) => {
                        const m = buildPartnerMeta(opt.data?.partner);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="indigo"
                          />
                        ) : null;
                      }}
                      labelRender={() => {
                        const m = buildPartnerMeta(selectedPartner);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="indigo"
                          />
                        ) : (
                          <span className="text-sm text-gray-400">Select partner</span>
                        );
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} sm={12}>
                  <Form.Item name="investor_id" label="Investor" className="mb-3!">
                    <Select
                      showSearch
                      autoClearSearchValue={false}
                      allowClear
                      disabled={!selectedPartnerUuid}
                      loading={loadingInvestors}
                      placeholder={selectedPartnerUuid ? 'Select investor' : 'Select partner first'}
                      optionFilterProp="label"
                      filterOption={false}
                      searchValue={investorSearchValue}
                      onSearch={handleInvestorSearch}
                      onPopupScroll={handleInvestorPopupScroll}
                      onOpenChange={handleInvestorOpenChange}
                      dropdownRender={investorDropdownRender}
                      onChange={handleInvestorChange}
                      className={ENTITY_SELECT_CLS}
                      options={investorOptions}
                      listHeight={360}
                      popupMatchSelectWidth
                      styles={{ popup: { root: { paddingBlock: 4 } } }}
                      optionRender={(opt) => {
                        const m = buildInvestorMeta(opt.data?.investor);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="primary"
                          />
                        ) : null;
                      }}
                      labelRender={() => {
                        const m = buildInvestorMeta(selectedInvestor);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            variant="primary"
                          />
                        ) : (
                          <span className="text-sm text-gray-400">Select investor</span>
                        );
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item name="product_id" label="Share" className="mb-0!">
                    <Select
                      showSearch
                      autoClearSearchValue={false}
                      allowClear
                      loading={loadingProducts}
                      placeholder="Pre-IPO share"
                      optionFilterProp="label"
                      filterOption={false}
                      searchValue={shareSearchValue}
                      onSearch={handleProductSearch}
                      onPopupScroll={handleProductPopupScroll}
                      onOpenChange={handleShareOpenChange}
                      dropdownRender={shareDropdownRender}
                      onChange={handleProductChange}
                      className={ENTITY_SELECT_CLS}
                      options={shareOptions}
                      listHeight={360}
                      popupMatchSelectWidth
                      styles={{ popup: { root: { paddingBlock: 4 } } }}
                      optionRender={(opt) => {
                        const m = buildShareMeta(opt.data?.product);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            imageUrl={m.imageUrl}
                            variant="primary"
                            imageRounded="md"
                          />
                        ) : null;
                      }}
                      labelRender={() => {
                        const m = buildShareMeta(selectedShare);
                        return m ? (
                          <StackedEntityOption
                            title={m.title}
                            line2={m.line2}
                            line3={m.line3}
                            imageUrl={m.imageUrl}
                            variant="primary"
                            imageRounded="md"
                          />
                        ) : (
                          <span className="text-sm text-gray-400">Pre-IPO share</span>
                        );
                      }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {/* ── Pricing section ── */}
              {pricingEnabled ? (
                <div className="mt-5 border-t border-gray-100 pt-5">
                  {pricingError ? (
                    <Alert
                      type="error" showIcon message={pricingError} className="mb-4"
                      action={<Button size="small" type="link" onClick={retryPricing}>Retry</Button>}
                    />
                  ) : null}

                  {pricingLoading && !hasResolveSuccess ? (
                    <Skeleton active paragraph={{ rows: 3 }} />
                  ) : hasResolveSuccess ? (
                    <>
                      {/* Exclusive callout */}
                      <ExclusiveCallout
                        specialPricing={specialPricing}
                        appliedSlab={appliedSlab}
                        onViewChart={() => setChartOpen(true)}
                      />

                      {/* Active tier bar */}
                      <TierBar
                        appliedSlab={appliedSlab}
                        basePartnerPrice={basePartnerPrice}
                        onViewChart={() => setChartOpen(true)}
                      />

                      {/* Input fields */}
                      <Row gutter={[12, 16]}>
                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="quantity" label="Quantity" className="mb-0!"
                            validateStatus={validationMessage ? 'error' : ''}
                            help={validationMessage || undefined}
                          >
                            <InputNumber
                              className="w-full" min={1} step={1} precision={0}
                              placeholder={qtyPlaceholder}
                              onChange={(val) => setQuantity(val == null ? '' : String(val))}
                              addonAfter={<span className="text-gray-500 text-sm">units</span>}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="partner_sell_price"
                            label="Partner Price"
                            className="mb-0!"
                            rules={
                              canEditPartnerPrice && !canBypassSlabLimits
                                ? [{
                                    validator: (_, value) => {
                                      if (!value) return Promise.resolve();
                                      const min = Number(appliedSlab?.pricing_per_unit);
                                      if (Number.isFinite(min) && Number(value) < min)
                                        return Promise.reject(`Min ${fmtInr(min)}`);
                                      return Promise.resolve();
                                    },
                                  }]
                                : []
                            }
                          >
                            {canEditPartnerPriceField ? (
                              <InputNumber
                                className="w-full"
                                prefix={<span className="text-gray-400">₹</span>}
                                controls
                                precision={2}
                                step={0.01}
                                placeholder="Enter price"
                                style={{ width: '100%' }}
                              />
                            ) : (
                              <ReadOnlyPriceInput />
                            )}
                          </Form.Item>
                        </Col>

                        <Col xs={24} sm={8}>
                          <Form.Item
                            name="investor_sell_price"
                            label="Investor Price"
                            className="mb-0!"
                            tooltip={
                              isPartnerSameAsInvestor
                                ? 'Partner and investor are the same — price matches partner price.'
                                : undefined
                            }
                            rules={
                              isPartnerSameAsInvestor
                                ? []
                                : [{
                                    validator: (_, value) => {
                                      if (!value) return Promise.resolve();
                                      const psp = form.getFieldValue('partner_sell_price');
                                      if (psp != null && Number(value) < Number(psp))
                                        return Promise.reject(`Min ${fmtInr(psp)}`);
                                      return Promise.resolve();
                                    },
                                  }]
                            }
                          >
                            <InputNumber
                              className={
                                isPartnerSameAsInvestor
                                  ? 'w-full partner-price-readonly [&_.ant-input-number-input]:bg-gray-50! [&_.ant-input-number-input]:text-gray-700! [&_.ant-input-number]:bg-gray-50! [&_.ant-input-number]:cursor-default!'
                                  : 'w-full'
                              }
                              prefix={<span className="text-gray-400">₹</span>}
                              precision={2}
                              step={0.01}
                              placeholder={isPartnerSameAsInvestor ? 'Same as partner' : '—'}
                              disabled={isPartnerSameAsInvestor}
                              readOnly={isPartnerSameAsInvestor}
                              controls={!isPartnerSameAsInvestor}
                              style={{ width: '100%' }}
                            />
                          </Form.Item>
                        </Col>
                      </Row>

                      {/* Summary + Place order */}
                      <OrderSummary
                        quantity={quantity}
                        partnerSellPrice={partnerSellPrice}
                        investorSellPrice={investorSellPrice}
                        canPlaceOrder={canPlaceOrder}
                        onPlaceOrder={handleOpenPlaceOrder}
                        placing={placingOrder}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}

            </Form>
          </div>
        </div>
      </div>

      <PriceChartModal
        open={chartOpen}
        onClose={() => setChartOpen(false)}
        pricing={pricing}
        specialPricing={specialPricing}
        appliedSlab={appliedSlab}
      />

      <PlaceOrderBankDematModal
        open={accountsModalOpen}
        onCancel={() => {
          if (!placingOrder) setAccountsModalOpen(false);
        }}
        investorId={selectedInvestorId}
        investorName={selectedInvestor?.name}
        onConfirm={handleConfirmPlaceOrder}
        confirming={placingOrder}
      />

      <PlaceOrderSuccessModal
        open={successModalOpen}
        investorName={placedOrderContext.investorName}
        onCreateNewOrder={resetOrderForm}
        onGoToTransactions={handleGoToTransactions}
      />
    </DashboardPageWrapper>
  );
}
